import useIsFeatureEnabled from "PFCore/helpers/use_is_feature_enabled";
import {
  Activity,
  ActivityProfile,
  Attachment,
  BookingCategory,
  FeatureFlag,
  PostUntilChangeReason,
  Template,
  TemplateConfiguration
} from "PFTypes";
import React, { Dispatch, SetStateAction } from "react";
import { useTranslation } from "react-i18next";

import { ExpiryDateSection } from "../sections/expiry_date_section/expiry_date_section";
import Assignee from "./assignee";
import { BookingCategorySection } from "./booking_category_section";
import CommonSideSection from "./common_side_section";
import ShowInterestQuestions from "./show_interest_questions";

type Assignee = ActivityProfile["profile"] | null;

type ActivityEditFormSidePanelProps = {
  activity: Activity;
  isEngagement: boolean;
  isRole: boolean;
  audiences: string[][];
  setAudiences: Dispatch<SetStateAction<string[][]>>;
  handlePostUntilChange: (postUntil: string) => void;
  postUntil?: string;
  template: Template;
  templateConfig?: TemplateConfiguration;
  errors: { interestQuestions?: string };
  assignee: Assignee;
  setAssignee: Dispatch<SetStateAction<Assignee>>;
  attachments: Attachment[];
  setAttachments: Dispatch<SetStateAction<Attachment[]>>;
  isPrivate: boolean;
  setIsPrivate: Dispatch<SetStateAction<boolean>>;
  communicationType: Activity["communication_type"];
  setCommunicationType: Dispatch<SetStateAction<Activity["communication_type"]>>;
  questionsToggleValue: boolean;
  handleUploadRemove: Dispatch<SetStateAction<boolean>>;
  questionsRef: React.MutableRefObject<
    {
      id: number;
      value: string;
    }[]
  >;
  showQuestionsTile: boolean;
  renderGrowlError: (message: string) => void;
  handleQuestionChange: (id: any, newValue: any) => void;
  handleQuestionsToggleChange: (newValue: boolean) => void;
  handlePrivateChange: () => void;
  bookingCategory: BookingCategory | null;
  setBookingCategory: Dispatch<SetStateAction<BookingCategory | null>>;
  qaIdPrefix: string;
  requireAssignee: boolean;
  postUntilChangeReason: PostUntilChangeReason;
  onPostUntilReasonChange: React.Dispatch<React.SetStateAction<PostUntilChangeReason | null>>;
};

export const ActivityEditFormSidePanel = ({
  activity,
  isEngagement,
  isRole,
  audiences,
  setAudiences,
  assignee,
  setAssignee,
  errors,
  attachments,
  setAttachments,
  handlePostUntilChange,
  postUntil,
  template,
  templateConfig,
  isPrivate,
  setIsPrivate,
  communicationType,
  setCommunicationType,
  questionsToggleValue,
  handleUploadRemove,
  questionsRef,
  showQuestionsTile,
  renderGrowlError,
  handleQuestionChange,
  handleQuestionsToggleChange,
  handlePrivateChange,
  bookingCategory,
  setBookingCategory,
  qaIdPrefix,
  requireAssignee,
  postUntilChangeReason,
  onPostUntilReasonChange
}: ActivityEditFormSidePanelProps) => {
  const isEnabled = useIsFeatureEnabled();
  const isBookingModuleEnabled = isEnabled(FeatureFlag.BookingModule);
  const { t } = useTranslation("activities");

  const isNew = !activity.id;
  const isEdit = !isNew;
  const isDraft = activity.state === "draft";
  const isForceVisibilitySet = !!template.force_visibility;
  const isForcePrivate = template.force_visibility === "private";
  const showBookingCategorySection =
    isBookingModuleEnabled && !!template.schema.properties.booking_category_id;

  return (
    <div>
      <ExpiryDateSection
        isEditMode={isEdit}
        template={template}
        postUntil={postUntil}
        onPostUntilChange={handlePostUntilChange}
        postUntilChangeReason={postUntilChangeReason}
        onPostUntilReasonChange={onPostUntilReasonChange}
        errors={errors}
        qaIdPrefix={qaIdPrefix}
      />
      {(isEngagement || isRole) && (
        <Assignee assignee={assignee} isRequired={requireAssignee} errors={errors} onChange={setAssignee} />
      )}
      {showBookingCategorySection && (
        <BookingCategorySection
          template={template}
          qaIdPrefix={qaIdPrefix}
          setBookingCategory={setBookingCategory}
          bookingCategory={bookingCategory}
          errors={errors}
        />
      )}
      <CommonSideSection
        template={template}
        activity={activity}
        attachments={attachments}
        audiences={audiences}
        qaIdPrefix={qaIdPrefix}
        handleUploadAdded={(attachment) => setAttachments([...attachments, attachment])}
        handleUploadSuccessful={(attachment) => setAttachments([...attachments, attachment])}
        handleUploadFailed={() => renderGrowlError(t("edit.failedUploadAttachment"))}
        handleUploadRemove={handleUploadRemove}
        isPrivate={isPrivate}
        setIsPrivate={handlePrivateChange}
        handleAudiencesChange={setAudiences}
        onPrivateChange={setIsPrivate}
        communicationType={communicationType}
        handleCommunicationChange={setCommunicationType}
        isForceVisibilitySet={isForceVisibilitySet}
        isForcePrivate={isForcePrivate}
        isDraft={isDraft}
        isNew={isNew}
        errors={errors}
      />
      {showQuestionsTile && (
        <ShowInterestQuestions
          isToggleVisible={isNew && !!templateConfig?.allow_rm_to_enable}
          toggleValue={questionsToggleValue}
          questions={questionsRef.current}
          areQuestionsEditable={isNew && !!templateConfig?.allow_rm_to_edit}
          errors={errors}
          onQuestionChange={handleQuestionChange}
          onToggleChange={handleQuestionsToggleChange}
        />
      )}
    </div>
  );
};
